import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, AlertTriangle, Clock } from 'lucide-react';

export const TrialNotificationDialog = () => {
  const [open, setOpen] = useState(false);
  const [trialInfo, setTrialInfo] = useState<{
    daysRemaining: number;
    title: string;
    description: string;
  } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar se já mostrou a notificação nesta sessão
    const shownThisSession = sessionStorage.getItem('trial_notification_shown');
    if (shownThisSession) return;

    const checkTrialStatus = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('check-subscription');
        
        // Handle session expiration errors silently
        if (error) {
          const errorMessage = error?.message || JSON.stringify(error);
          if (errorMessage.includes('Session') && errorMessage.includes('does not exist')) {
            console.warn('Session expired in TrialNotificationDialog, signing out...');
            await supabase.auth.signOut();
            return;
          }
          throw error;
        }
        
        if (!data) return;

        // Só notifica se não estiver com assinatura ativa
        if (data.subscribed) return;

        // Verifica se tem trial ativo
        if (data.trial_ends_at) {
          const trialEnd = new Date(data.trial_ends_at);
          const now = new Date();
          const daysRemaining = differenceInDays(trialEnd, now);

          // Só notifica se faltar 3 dias ou menos E ainda tiver trial ativo
          if (daysRemaining >= 0 && daysRemaining <= 3) {
            const messages: Record<number, { title: string; description: string }> = {
              0: {
                title: 'Último dia do período de teste!',
                description: 'Seu trial expira hoje. Assine agora para continuar usando todas as funcionalidades do Obrim.',
              },
              1: {
                title: '1 dia restante do período de teste',
                description: 'Seu trial expira amanhã. Não perca o acesso às suas obras! Assine agora e continue gerenciando seus projetos.',
              },
              2: {
                title: '2 dias restantes do período de teste',
                description: 'Seu trial está acabando. Assine agora e continue gerenciando suas obras com todas as funcionalidades.',
              },
              3: {
                title: '3 dias restantes do período de teste',
                description: 'Seu período de teste está chegando ao fim. Aproveite para conhecer todos os planos e garantir acesso contínuo.',
              },
            };

            const message = messages[daysRemaining];
            
            if (message) {
              setTrialInfo({
                daysRemaining,
                title: message.title,
                description: message.description,
              });
              setOpen(true);
              sessionStorage.setItem('trial_notification_shown', 'true');
            }
          }

          // Se o trial já expirou
          if (daysRemaining < 0) {
            setTrialInfo({
              daysRemaining: -1,
              title: 'Período de teste expirado',
              description: 'Seu trial acabou. Assine agora para continuar usando o Obrim e gerenciando seus projetos.',
            });
            setOpen(true);
            sessionStorage.setItem('trial_notification_shown', 'true');
          }
        }
      } catch (error: any) {
        // Handle session errors in catch as well
        const errorMessage = error?.message || String(error);
        if (errorMessage.includes('Session') && errorMessage.includes('does not exist')) {
          console.warn('Session expired in TrialNotificationDialog catch, signing out...');
          await supabase.auth.signOut();
          return;
        }
        console.error('Erro ao verificar status do trial:', error);
      }
    };

    checkTrialStatus();
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const handleViewPlans = () => {
    setOpen(false);
    navigate('/assinatura');
  };

  if (!trialInfo) return null;

  const isExpired = trialInfo.daysRemaining < 0;
  const isUrgent = trialInfo.daysRemaining <= 1;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Fechar</span>
        </button>
        
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            {isExpired || isUrgent ? (
              <div className="p-2 rounded-full bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
            ) : (
              <div className="p-2 rounded-full bg-warning/10">
                <Clock className="h-6 w-6 text-warning" />
              </div>
            )}
          </div>
          <DialogTitle className="text-2xl">
            {trialInfo.title}
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            {trialInfo.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col gap-3 mt-4">
          <Button 
            onClick={handleViewPlans}
            className="w-full"
            size="lg"
          >
            {isExpired ? 'Assinar Agora' : 'Ver Planos'}
          </Button>
          <Button 
            onClick={handleClose}
            variant="outline"
            className="w-full"
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
